<template>
  <div
    v-if="images && images.length > 0"
    class="my-6 flex flex-col gap-4 items-center"
    data-disable-swipe-navigation="true"
    :data-blok-c="dataBlokC"
    :data-blok-uid="dataBlokUid"
  >
    <BaseSwiper
      v-if="swiperReady"
      :id="swiperId"
      :ref="swiperId"
      :items="images"
      :options="swiperOptions"
      :gap="0"
      class="w-full carousel-swiper"
      @active-index-change="handleSlideChange"
    >
      <template #default="slotProps">
        <div
          class="flex flex-col gap-[4px] items-center justify-center relative shrink-0 w-full"
          :class="wrapperClasses"
        >
          <div
            class="relative w-full overflow-hidden rounded-[10px]"
            :class="{ 'bg-black': isVideo(slotProps.item) }"
            :style="getAspectStyle(slotProps.item, slotProps.index)"
          >
            <!-- Video element -->
            <video
              v-if="isVideo(slotProps.item)"
              :ref="el => setVideoRef(slotProps.index, el)"
              :src="getVideoUrl(slotProps.item)"
              :poster="getPosterUrl(slotProps.item)"
              class="absolute inset-0 w-full h-full object-contain rounded-[10px] video-element"
              preload="metadata"
              controls
              controlsList="nodownload nofullscreen noremoteplayback"
              playsinline
              disablePictureInPicture
              @loadedmetadata="onVideoMetadata(slotProps.index, $event)"
              @play="onVideoPlay(slotProps.index)"
              @pause="onVideoPause(slotProps.index)"
              @click="onVideoTap(slotProps.index, $event)"
              @contextmenu.prevent
            ></video>
            <!-- Image element -->
            <img
              v-else
              :src="getOptimizedImageUrl(slotProps.item)"
              :alt="getImageAlt(slotProps.item, slotProps.index)"
              class="absolute inset-0 w-full h-full object-cover rounded-[10px]"
              loading="lazy"
              @error="handleImageError($event, slotProps.item)"
            />
            <!-- Play button overlay — only shown before first play -->
            <div
              v-if="isVideo(slotProps.item) && !startedVideos[slotProps.index]"
              class="absolute bg-[#e7b425] box-border flex gap-[8px] items-center left-1/2 pl-[9px] pr-[7px] py-[8px] rounded-[100px] shadow-[0px_10px_13px_2px_rgba(12,12,13,0.2)] top-1/2 -translate-x-1/2 -translate-y-1/2 cursor-pointer z-10"
              @click.stop="playVideo(slotProps.index)"
            >
              <svg
                class="w-[24px] h-[24px]"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M8 5V19L19 12L8 5Z" fill="white" />
              </svg>
            </div>
          </div>
          <p
            v-if="getImageCaption(slotProps.item)"
            class="text-[0.75rem] leading-[1rem] text-[#353535] text-center tracking-[-0.12px] w-full"
          >
            {{ getImageCaption(slotProps.item) }}
          </p>
        </div>
      </template>

      <template #prevArrow>
        <div class="absolute left-2 top-1/2 -translate-y-1/2 z-20 cursor-pointer">
          <svg
            width="32"
            height="32"
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect width="32" height="32" rx="16" fill="#F2EFED" />
            <path
              d="M20.6663 15.9997H11.333M11.333 15.9997L15.9997 20.6663M11.333 15.9997L15.9997 11.333"
              stroke="#191817"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
      </template>

      <template #nextArrow>
        <div class="absolute right-2 top-1/2 -translate-y-1/2 z-20 cursor-pointer">
          <svg
            width="32"
            height="32"
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect width="32" height="32" rx="16" fill="#F2EFED" />
            <path
              d="M11.3337 15.9997H20.6663M20.6663 15.9997L16.0003 20.6663M20.6663 15.9997L16.0003 11.333"
              stroke="#191817"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
      </template>
    </BaseSwiper>

    <Pagination
      v-if="images.length > 1"
      :total-pages="images.length"
      :current-page="currentIndex"
      passive-color="#D3D8DE"
      active-color="#6B5744"
    />
  </div>
</template>

<script>
import BaseSwiper from '@/components/design/BaseSwiper.vue';
import Pagination from '@/components/design/Pagination.vue';
import { optimizeStoryblokImage, getRetryUrl, resolveStoryblokUrl } from '@/utils/storyblok-image';
import { SINGLE_ITEM } from '@/utils/swiper-presets';

const VIDEO_EXTENSIONS = ['.mp4', '.mov', '.webm', '.ogg', '.avi', '.m4v'];
const DEFAULT_POSTER_SVG = `<svg width="640" height="360" xmlns="http://www.w3.org/2000/svg">
  <rect width="640" height="360" fill="#000000"/>
</svg>`;

export default {
  name: 'ContentCarousel',
  components: {
    BaseSwiper,
    Pagination,
  },
  props: {
    images: {
      type: Array,
      default: () => [],
    },
    aspectRatio: {
      type: String,
      default: 'wide',
      validator: value => ['square', 'wide', 'tall'].includes(value),
    },
    caption: {
      type: String,
      default: '',
    },
    dataBlokC: {
      type: String,
      default: '',
    },
    dataBlokUid: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      currentIndex: 1,
      swiperId: `carousel-${Math.random().toString(36).substr(2, 9)}`,
      swiperReady: false,
      playingVideos: {},
      startedVideos: {},
      videoNaturalAspects: {},
      videoRefs: {},
      touchStartX: 0,
      touchStartY: 0,
      generatedPosters: {},
      imageRetries: {},
    };
  },
  computed: {
    swiperOptions() {
      return {
        ...SINGLE_ITEM,
        allowTouchMove: true,
        touchEventsTarget: 'container',
        hasNavigation: this.images.length > 1,
      };
    },
    wrapperClasses() {
      const widthMap = {
        square: 'w-[280px]',
        wide: 'w-full',
        tall: 'w-[240px]',
      };
      return widthMap[this.aspectRatio] || widthMap.wide;
    },
    aspectStyle() {
      // Use padding-bottom technique instead of CSS aspect-ratio for reliable
      // rendering in iOS WKWebView and Android WebView where aspect-ratio
      // can fail on overflow:hidden containers with absolutely-positioned children.
      const paddingMap = {
        square: 100, // 1:1
        wide: 56.12, // 188/335 * 100
        tall: 177.78, // 16/9 * 100
      };
      return {
        paddingBottom: `${paddingMap[this.aspectRatio] || paddingMap.wide}%`,
        height: '0',
      };
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.setupSwipeHandlers();
      this.generateAllPosters();
    });
    // Defer Swiper initialization by one animation frame so the browser
    // has performed layout and clientWidth is non-zero when Swiper reads it.
    requestAnimationFrame(() => {
      this.swiperReady = true;
    });
  },
  beforeUnmount() {
    this.removeSwipeHandlers();
  },
  methods: {
    getAspectStyle(item, index) {
      // Once video metadata is available, use the actual aspect ratio
      // so the container fits the video without black bars.
      // Before metadata loads (iOS delays until interaction), fall back
      // to the prop-based default.
      if (this.isVideo(item) && this.videoNaturalAspects[index]) {
        return {
          paddingBottom: `${this.videoNaturalAspects[index]}%`,
          height: '0',
        };
      }
      return this.aspectStyle;
    },
    onVideoMetadata(index, event) {
      const video = event.target;
      if (video.videoWidth && video.videoHeight) {
        this.videoNaturalAspects = {
          ...this.videoNaturalAspects,
          [index]: (video.videoHeight / video.videoWidth) * 100,
        };
      }
    },
    handleSlideChange(isEnd, activeIndex) {
      this.pauseAllVideos();
      this.currentIndex = activeIndex;
    },
    setupSwipeHandlers() {
      const swiperContainer = this.$el?.querySelector('.carousel-swiper') || this.$el;
      if (!swiperContainer) return;

      let touchStartX = 0;
      let touchStartY = 0;

      const handleTouchStart = e => {
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
      };

      const handleTouchMove = e => {
        if (!touchStartX || !touchStartY) return;

        const deltaX = Math.abs(e.touches[0].clientX - touchStartX);
        const deltaY = Math.abs(e.touches[0].clientY - touchStartY);

        if (deltaX > 10 && deltaX > deltaY) {
          this.pauseAllVideos();
        }
      };

      const handleTouchEnd = () => {
        touchStartX = 0;
        touchStartY = 0;
      };

      swiperContainer.addEventListener('touchstart', handleTouchStart, { passive: true });
      swiperContainer.addEventListener('touchmove', handleTouchMove, { passive: true });
      swiperContainer.addEventListener('touchend', handleTouchEnd, { passive: true });

      this._swipeHandlers = {
        container: swiperContainer,
        touchStart: handleTouchStart,
        touchMove: handleTouchMove,
        touchEnd: handleTouchEnd,
      };
    },
    removeSwipeHandlers() {
      if (this._swipeHandlers) {
        const { container, touchStart, touchMove, touchEnd } = this._swipeHandlers;
        container.removeEventListener('touchstart', touchStart);
        container.removeEventListener('touchmove', touchMove);
        container.removeEventListener('touchend', touchEnd);
        this._swipeHandlers = null;
      }
    },
    pauseAllVideos() {
      for (const video of Object.values(this.videoRefs)) {
        if (video && !video.paused) video.pause();
      }
    },
    isVideo(imageData) {
      if (!imageData) return false;

      if (
        typeof imageData === 'object' &&
        (imageData.isVideo || imageData.type === 'video' || imageData.videoUrl)
      ) {
        return true;
      }

      const url = this.getImageUrl(imageData);
      if (url && typeof url === 'string') {
        const lowerUrl = url.toLowerCase();
        return VIDEO_EXTENSIONS.some(
          ext =>
            lowerUrl.endsWith(ext) || lowerUrl.includes(`${ext}?`) || lowerUrl.includes(`${ext}#`),
        );
      }

      return false;
    },
    getVideoUrl(imageData) {
      if (!imageData) return '';

      if (typeof imageData === 'object' && imageData.videoUrl) {
        return imageData.videoUrl;
      }

      return this.getImageUrl(imageData);
    },
    getPosterUrl(imageData) {
      if (typeof imageData === 'object') {
        const poster = imageData.poster || imageData.thumbnail || imageData.posterUrl;
        if (poster) return resolveStoryblokUrl(poster);
      }

      const videoUrl = this.getVideoUrl(imageData);
      if (!videoUrl) return this.getDefaultPoster();

      return this.generatedPosters[videoUrl] || this.getDefaultPoster();
    },
    getDefaultPoster() {
      return `data:image/svg+xml;base64,${btoa(DEFAULT_POSTER_SVG)}`;
    },
    async generateVideoPoster(videoUrl) {
      try {
        const video = document.createElement('video');
        video.crossOrigin = 'anonymous';
        video.preload = 'metadata';
        video.src = videoUrl;

        await new Promise((resolve, reject) => {
          video.addEventListener('loadedmetadata', resolve);
          video.addEventListener('error', reject);
          setTimeout(reject, 5000);
        });

        video.currentTime = 0.1;

        await new Promise(resolve => {
          video.addEventListener('seeked', resolve, { once: true });
        });

        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth || 640;
        canvas.height = video.videoHeight || 360;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        const posterUrl = canvas.toDataURL('image/jpeg', 0.85);
        video.src = '';
        video.load();

        return posterUrl;
      } catch (err) {
        console.warn('[Carousel] Failed to generate video poster:', videoUrl, err);
        return null;
      }
    },
    setVideoRef(index, el) {
      if (el) {
        this.videoRefs[index] = el;
      }
    },
    isVideoPlaying(index) {
      return this.playingVideos[index] || false;
    },
    onVideoTap(index) {
      // Only handle tap-to-toggle after the video has started playing.
      if (!this.startedVideos[index]) return;
      // If native controls just triggered a play/pause (within 300ms),
      // skip to avoid double-toggling.
      if (this._lastNativeToggle && Date.now() - this._lastNativeToggle < 300) return;
      const video = this.videoRefs[index];
      if (!video) return;
      if (video.paused) {
        video.play();
      } else {
        video.pause();
      }
    },
    onVideoPlay(index) {
      this._lastNativeToggle = Date.now();
      this.playingVideos[index] = true;
      this.startedVideos[index] = true;
    },
    onVideoPause(index) {
      this._lastNativeToggle = Date.now();
      this.playingVideos[index] = false;
    },
    playVideo(index) {
      const video = this.videoRefs[index];
      if (video) {
        video.play();
      }
    },
    toggleVideoPlayback(index) {
      const video = this.videoRefs[index];
      if (video) {
        if (video.paused) {
          video.play();
        } else {
          video.pause();
        }
      }
    },
    onVideoTouchStart(_index, event) {
      if (!event.touches?.length) return;
      this.touchStartX = event.touches[0].clientX;
      this.touchStartY = event.touches[0].clientY;
    },
    onVideoTouchMove(index, event) {
      if (!this.touchStartX || !this.touchStartY || !event.touches?.length) return;

      const deltaX = Math.abs(event.touches[0].clientX - this.touchStartX);
      const deltaY = Math.abs(event.touches[0].clientY - this.touchStartY);

      if (deltaX > 10 && deltaX > deltaY) {
        const video = this.videoRefs[index];
        if (video && !video.paused) video.pause();
      }
    },
    onVideoTouchEnd() {
      this.touchStartX = 0;
      this.touchStartY = 0;
    },
    async generateAllPosters() {
      if (!this.images?.length) return;

      const posterPromises = this.images
        .filter(imageData => {
          if (!this.isVideo(imageData)) return false;
          if (
            typeof imageData === 'object' &&
            (imageData.poster || imageData.thumbnail || imageData.posterUrl)
          )
            return false;
          return !!this.getVideoUrl(imageData);
        })
        .map(async imageData => {
          const videoUrl = this.getVideoUrl(imageData);
          const poster = await this.generateVideoPoster(videoUrl);
          if (poster) this.generatedPosters[videoUrl] = poster;
        });

      await Promise.allSettled(posterPromises);
    },
    getOptimizedImageUrl(imageData) {
      return optimizeStoryblokImage(this.getImageUrl(imageData), 800);
    },
    handleImageError(event, imageData) {
      const originalUrl = this.getImageUrl(imageData);
      const retryCount = (this.imageRetries[originalUrl] || 0) + 1;
      this.imageRetries[originalUrl] = retryCount;
      const retry = getRetryUrl(originalUrl, retryCount);
      if (retry) {
        setTimeout(() => {
          event.target.src = optimizeStoryblokImage(retry.url, 800);
        }, retry.delay);
      }
    },
    getImageUrl(imageData) {
      if (!imageData) return '';
      if (typeof imageData === 'string') return imageData;
      // Prefer an external/S3 URL (positional imageUrls field) over the asset.
      if (imageData.externalUrl?.trim()) return imageData.externalUrl.trim();
      if (imageData.filename) return resolveStoryblokUrl(imageData.filename);
      if (imageData.url) return imageData.url;
      if (imageData.image?.filename) return resolveStoryblokUrl(imageData.image.filename);
      return '';
    },
    getImageAlt(imageData, index) {
      if (typeof imageData === 'string') return `Image ${index + 1}`;
      return imageData.alt || imageData.altText || imageData.image?.alt || `Image ${index + 1}`;
    },
    getImageCaption(imageData) {
      if (typeof imageData === 'string') return '';
      return (
        imageData.caption ||
        imageData.label ||
        imageData.name ||
        imageData.title ||
        imageData.image?.title ||
        imageData.image?.name ||
        imageData.picture?.title ||
        imageData.picture?.name ||
        ''
      );
    },
  },
};
</script>

<style scoped>
* {
  font-family: 'Inter', 'aktiv-grotesk', sans-serif;
}

.video-element {
  touch-action: pan-x pan-y pinch-zoom;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  pointer-events: auto;
}

.video-element::-webkit-media-controls {
  pointer-events: auto;
}

.video-element::-webkit-media-controls-play-button,
.video-element::-webkit-media-controls-pause-button,
.video-element::-webkit-media-controls-timeline,
.video-element::-webkit-media-controls-volume-slider {
  pointer-events: auto;
}

.video-element::-webkit-media-controls-download-button {
  display: none !important;
}
</style>
